import React, { useState, useEffect, useCallback } from "react";
import styled, { keyframes } from "styled-components";
import { Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  ArcElement,
} from "chart.js";
import axios from "axios";
import { useSnackbar } from "notistack";
import { FiRefreshCw } from "react-icons/fi";

ChartJS.register(BarElement, Tooltip, Legend, CategoryScale, LinearScale, ArcElement);

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const ChartsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 2rem;
  position: relative;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const ChartWrapper = styled.div`
  min-height: 400px;
  position: relative;
`;

const ChartCard = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  height: 100%;
  display: flex;
  flex-direction: column;
  animation: ${fadeIn} 0.5s ease forwards;
  position: relative;
  overflow: hidden;
  z-index: 1;
  will-change: transform, opacity;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0) 100%);
    z-index: -1;
  }
`;

const ChartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;

  h3 {
    font-size: 1.2rem;
    font-weight: 600;
    color: #1a1a1a;
  }
`;

const ChartActions = styled.div`
  display: flex;
  align-items: center;
  
  select {
    padding: 0.5rem;
    border-radius: 6px;
    border: 1px solid #ddd;
    font-size: 0.9rem;
    cursor: pointer;
    background: #fff;
    transition: all 0.3s ease;

    &:hover {
      border-color: #007aff;
    }
  }
`;

const ChartContainer = styled.div`
  position: relative;
  flex: 1;
  min-height: 300px;
`;

const RefreshButton = styled.button`
  background: transparent;
  border: none;
  color: #666;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  margin-left: 1rem;
  transition: color 0.3s ease;

  &:hover {
    color: #007aff;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SkeletonLoader = styled.div`
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 8px;
  height: ${props => props.height || '20px'};
  width: ${props => props.width || '100%'};
  margin-bottom: ${props => props.marginBottom || '0'};

  @keyframes shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`;

const ChartsSection = ({ activeFilter, setActiveFilter, userPlan }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [relocationProgress, setRelocationProgress] = useState({
    labels: [],
    datasets: []
  });
  const [popularDestinations, setPopularDestinations] = useState({
    labels: [],
    data: []
  });
  const [loadingProgress, setLoadingProgress] = useState(true);
  const [loadingDestinations, setLoadingDestinations] = useState(true);

  const buildApiUrl = (endpoint) => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:8087";
    return `${baseUrl.replace(/\/+$/, "")}/${endpoint.replace(/^\/+/, "")}`;
  };

  const mapPeriodToApiParam = useCallback((period) => {
    const periodMap = {
      'diario': 'daily',
      'semanal': 'weekly',
      'mensal': 'monthly',
      'anual': 'yearly',
      'trimestral': 'quarterly'
    };
    return periodMap[period] || 'monthly';
  }, []);

  const getCompleteMonthLabels = useCallback((dataLabels, filter) => {
    if (filter !== 'mensal') return dataLabels;

    const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    
    if (dataLabels && dataLabels.length > 0) {
      return dataLabels.map(label => {
        if (typeof label === 'string' && label.includes('/')) {
          const monthNumber = parseInt(label.split('/')[0]) - 1;
          return monthNames[monthNumber] || label;
        }
        return label;
      });
    }

    return monthNames;
  }, []);

  const fetchRelocationProgress = useCallback(async (period) => {
    try {
      setLoadingProgress(true);
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const token = user.token;

      if (!token) {
        throw new Error("Usuário não autenticado");
      }

      const apiPeriod = mapPeriodToApiParam(period);
      const response = await axios.get(buildApiUrl("dashboard/relocation-progress"), {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json"
        },
        params: { period: apiPeriod }
      });

      const processedData = {
        labels: getCompleteMonthLabels(response.data.labels, period),
        datasets: [{
          ...response.data.datasets[0],
          data: response.data.datasets[0].data || []
        }]
      };

      const formattedData = {
        labels: processedData.labels,
        datasets: [
          {
            label: processedData.datasets[0].label || "Total por Mês",
            data: processedData.datasets[0].data,
            backgroundColor: "#4285F4",
            borderRadius: 8,
            borderSkipped: false,
          }
        ]
      };

      setRelocationProgress(formattedData);
    } catch (error) {
      console.error("Erro ao carregar progresso de realocações:", error);
      enqueueSnackbar(
        error.response?.data?.message || "Erro ao carregar progresso de realocações", 
        { variant: "error" }
      );
    } finally {
      setLoadingProgress(false);
    }
  }, [enqueueSnackbar, mapPeriodToApiParam, getCompleteMonthLabels]);

  const fetchPopularDestinations = useCallback(async (period) => {
    try {
      setLoadingDestinations(true);
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const token = user.token;

      if (!token) {
        throw new Error("Usuário não autenticado");
      }

      const apiPeriod = mapPeriodToApiParam(period);
      const response = await axios.get(buildApiUrl("dashboard/popular-destinations"), {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json"
        },
        params: { period: apiPeriod }
      });

      setPopularDestinations({
        labels: response.data.labels || [],
        data: response.data.data || []
      });
    } catch (error) {
      console.error("Erro ao carregar destinos populares:", error);
      enqueueSnackbar(
        error.response?.data?.message || "Erro ao carregar destinos populares", 
        { variant: "error" }
      );
    } finally {
      setLoadingDestinations(false);
    }
  }, [enqueueSnackbar, mapPeriodToApiParam]);

  useEffect(() => {
    if (userPlan !== 'Free') {
      fetchRelocationProgress(activeFilter);
      fetchPopularDestinations(activeFilter);
    }
  }, [activeFilter, userPlan, fetchRelocationProgress, fetchPopularDestinations]);

  const handleRefreshProgress = async () => {
    await fetchRelocationProgress(activeFilter);
    enqueueSnackbar('Progresso atualizado!', { variant: 'success' });
  };

  const handleRefreshDestinations = async () => {
    await fetchPopularDestinations(activeFilter);
    enqueueSnackbar('Destinos atualizados!', { variant: 'success' });
  };

  const doughnutData = {
    labels: popularDestinations.labels,
    datasets: [
      {
        data: popularDestinations.data,
        backgroundColor: [
          "#4285F4", 
          "#EA4335", 
          "#FBBC05", 
          "#34A853", 
          "#673AB7",
          "#FF6D00",
          "#2962FF",
          "#00BFA5"
        ],
        borderWidth: 0,
      },
    ],
  };

  if (userPlan === 'Free') return null;

  return (
    <ChartsContainer>
      <ChartWrapper>
        <ChartCard>
          <ChartHeader>
            <h3>
              {activeFilter === 'mensal' ? 'Progresso Mensal de Realocações' : 
              activeFilter === 'semanal' ? 'Progresso Semanal de Realocações' :
               activeFilter === 'anual' ? 'Progresso Anual de Realocações' :
               activeFilter === 'diario' ? 'Progresso Diário de Realocações' :
               'Progresso Anual de Realocações'}
            </h3>
            <ChartActions>
              <select 
                value={activeFilter} 
                onChange={(e) => {
                  e.preventDefault();
                  setActiveFilter(e.target.value);
                }}
              >
                <option value="mensal">Mensal</option>
                <option value="diario">Diário</option>
                <option value="semanal">Semanal</option>
                <option value="anual">Anual</option>
              </select>
              <RefreshButton 
                onClick={handleRefreshProgress} 
                disabled={loadingProgress}
                aria-label="Atualizar progresso"
              >
                <FiRefreshCw size={14} />
              </RefreshButton>
            </ChartActions>
          </ChartHeader>
          <ChartContainer>
            {loadingProgress ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                height: '100%',
                gap: '10px'
              }}>
                <SkeletonLoader height="20px" width="80%" marginBottom="10px" />
                <SkeletonLoader height="250px" />
              </div>
            ) : (
              <Bar
                data={relocationProgress}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'top',
                    },
                    tooltip: {
                      callbacks: {
                        label: function(context) {
                          return `${context.dataset.label}: ${context.raw}`;
                        }
                      }
                    }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      grid: {
                        drawBorder: false,
                      }
                    },
                    x: {
                      grid: {
                        display: false,
                      }
                    }
                  }
                }}
              />
            )}
          </ChartContainer>
        </ChartCard>
      </ChartWrapper>

      <ChartWrapper>
        <ChartCard>
          <ChartHeader>
            <h3>Destinos Mais Populares</h3>
            <RefreshButton 
              onClick={handleRefreshDestinations} 
              disabled={loadingDestinations}
              aria-label="Atualizar destinos"
            >
              <FiRefreshCw size={14} />
            </RefreshButton>
          </ChartHeader>
          <ChartContainer>
            {loadingDestinations ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                height: '100%',
                gap: '10px'
              }}>
                <SkeletonLoader height="20px" width="80%" marginBottom="10px" />
                <SkeletonLoader height="250px" />
              </div>
            ) : (
              <Doughnut
                data={doughnutData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'right',
                    },
                  },
                  cutout: '70%',
                }}
              />
            )}
          </ChartContainer>
        </ChartCard>
      </ChartWrapper>
    </ChartsContainer>
  );
};

export default React.memo(ChartsSection);